import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ButtonComponent,
  ConfirmDialogService,
  ToastService,
} from '@dental-ui/ui';
import {
  LucideChevronLeft,
  LucideChevronRight,
  LucidePencil,
  LucidePlus,
  LucideRefreshCw,
  LucideSearch,
  LucideTrash2,
  LucideX,
} from '@lucide/angular';
import {
  PortalPackageContentsApiService,
  FeatureDto,
} from '../data-access/portal-package-contents-api.service';

const PAGE_SIZE = 10;

@Component({
  selector: 'lib-portal-package-contents',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    LucideChevronLeft,
    LucideChevronRight,
    LucidePencil,
    LucidePlus,
    LucideRefreshCw,
    LucideSearch,
    LucideTrash2,
    LucideX,
  ],
  templateUrl: './portal-package-contents.component.html',
  styleUrl: './portal-package-contents.component.scss',
})
export class PortalPackageContentsComponent implements OnInit {
  private readonly api = inject(PortalPackageContentsApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<FeatureDto[]>([]);
  readonly loading = signal(false);
  readonly totalPages = signal(0);
  readonly page = signal(0);
  searchTerm = '';

  readonly showFormDialog = signal(false);
  readonly formSaving = signal(false);
  readonly isEdit = signal(false);
  readonly formModel = signal<FeatureDto>({
    id: '',
    name: '',
    code: '',
    isActive: true,
    currencyType: 'USD',
    monthlyPrice: 0,
    yearlyPrice: 0,
    monthlyDiscount: 0,
    yearlyDiscount: 0,
    totalMonthlyPrice: 0,
    totalYearlyPrice: 0,
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getFeatures(this.page(), PAGE_SIZE, this.searchTerm).subscribe({
      next: (res) => {
        this.items.set(res.data);
        this.totalPages.set(res.totalPage);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Paket içerikleri yüklenirken hata oluştu');
      },
    });
  }

  onSearch(): void {
    this.page.set(0);
    this.load();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.page.set(0);
    this.load();
  }

  prevPage(): void {
    if (this.page() > 0) {
      this.page.update((p) => p - 1);
      this.load();
    }
  }

  nextPage(): void {
    if (this.page() < this.totalPages() - 1) {
      this.page.update((p) => p + 1);
      this.load();
    }
  }

  openAdd(): void {
    this.isEdit.set(false);
    this.formModel.set({
      id: '',
      name: '',
      code: '',
      isActive: true,
      currencyType: 'USD',
      monthlyPrice: 0,
      yearlyPrice: 0,
      monthlyDiscount: 0,
      yearlyDiscount: 0,
      totalMonthlyPrice: 0,
      totalYearlyPrice: 0,
    });
    this.showFormDialog.set(true);
  }

  openEdit(item: FeatureDto): void {
    this.isEdit.set(true);
    this.formModel.set({
      ...item,
      monthlyPrice: item.monthlyPrice || 0,
      yearlyPrice: item.yearlyPrice || 0,
      monthlyDiscount: item.monthlyDiscount || 0,
      yearlyDiscount: item.yearlyDiscount || 0,
      totalMonthlyPrice: item.totalMonthlyPrice || 0,
      totalYearlyPrice: item.totalYearlyPrice || 0,
      currencyType: item.currencyType || 'USD',
    });
    this.showFormDialog.set(true);
  }

  closeFormDialog(): void {
    this.showFormDialog.set(false);
  }

  calcTotals(): void {
    const m = this.formModel();
    const mPrice = m.monthlyPrice || 0;
    const yPrice = m.yearlyPrice || 0;
    const mDisc = m.monthlyDiscount || 0;
    const yDisc = m.yearlyDiscount || 0;
    const monthly = mPrice - (mPrice * Math.min(Math.max(mDisc, 0), 100)) / 100;
    const yearly = yPrice - (yPrice * Math.min(Math.max(yDisc, 0), 100)) / 100;
    this.formModel.update((prev) => ({
      ...prev,
      totalMonthlyPrice: Math.round(monthly * 100) / 100,
      totalYearlyPrice: Math.round(yearly * 100) / 100,
    }));
  }

  updateField<K extends keyof FeatureDto>(key: K, value: FeatureDto[K]): void {
    this.formModel.update((prev) => ({ ...prev, [key]: value }));
    this.calcTotals();
  }

  save(): void {
    const model = this.formModel();
    this.formSaving.set(true);
    const call = this.isEdit()
      ? this.api.updateFeature(model)
      : this.api.createFeature(model);

    call.subscribe({
      next: () => {
        this.formSaving.set(false);
        this.toast.success(this.isEdit() ? 'İçerik güncellendi' : 'İçerik eklendi');
        this.showFormDialog.set(false);
        this.load();
      },
      error: () => {
        this.formSaving.set(false);
        this.toast.error('İşlem sırasında hata oluştu');
      },
    });
  }

  delete(item: FeatureDto): void {
    this.confirmDialog
      .confirm({
        title: 'İçeriği Sil',
        message: `"${item.name}" içeriğini silmek istediğinizden emin misiniz?`,
        confirmText: 'Sil',
        cancelText: 'İptal',
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.api.deleteFeature(item.id).subscribe({
          next: () => {
            this.toast.success('İçerik silindi');
            this.load();
          },
          error: () => this.toast.error('Silme işlemi başarısız'),
        });
      });
  }

  formatPrice(value: number | null | undefined, currency: string): string {
    if (value === null || value === undefined) {
      return `0.00 ${currency || 'USD'}`;
    }
    return `${Number(value).toFixed(2)} ${currency || 'USD'}`;
  }
}
